// /welcome — conversational onboarding. Siggy walks new users
// through adding their first few medications via natural language
// instead of a form. Uses /api/onboarding/parse-med to extract
// {name, strength, frequency} from the user's free-text reply, then
// POSTs to /api/medications on confirmation.

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChatMarkdown } from '../components/ChatMarkdown';
import { SiggyAvatar } from '../components/SiggyAvatar';
import { SeniorButton } from '../components/senior/SeniorButton';
import { useAuthedFetch } from '../hooks/useAuthedFetch';
import { useSpeechRecognition } from '../hooks/useSpeechRecognition';
import { AppPage } from '../layouts/AppPage';

interface ParsedMed {
  name: string;
  strength: string | null;
  frequency: string | null;
  confidence: 'high' | 'medium' | 'low';
}

type Turn =
  | { id: string; role: 'siggy'; text: string }
  | { id: string; role: 'user'; text: string }
  | { id: string; role: 'parse'; parsed: ParsedMed; pendingText: string };

const INTRO_TURNS: Array<{ id: string; role: 'siggy'; text: string }> = [
  {
    id: 'intro-1',
    role: 'siggy',
    text: "Hi, I'm Siggy. Welcome to MedReview.",
  },
  {
    id: 'intro-2',
    role: 'siggy',
    text: "Let's set up your medication list together. Tell me the first medicine you take. You can say the name only, or include the dose and how often. For example, '**Lipitor 20 milligrams once a day**'.",
  },
];

export function Welcome(): JSX.Element {
  const navigate = useNavigate();
  const authedFetch = useAuthedFetch();
  const speech = useSpeechRecognition();
  const [turns, setTurns] = useState<Turn[]>(INTRO_TURNS);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addedCount, setAddedCount] = useState(0);
  const endRef = useRef<HTMLDivElement | null>(null);
  const composerRef = useRef<HTMLInputElement | null>(null);
  const baseline = useRef('');

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    // Auto-focus the composer on first paint so the user can start
    // typing right away. Skip on touch devices where focusing the
    // input would pop the keyboard up and obscure Siggy's first
    // message.
    const isTouch =
      typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;
    if (!isTouch) {
      composerRef.current?.focus();
    }
  }, []);

  const appendSiggy = (text: string) =>
    setTurns((prev) => [
      ...prev,
      { id: `m-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, role: 'siggy', text },
    ]);

  async function handleSubmit() {
    const text = draft.trim();
    if (!text || busy) return;
    setDraft('');
    setError(null);
    setTurns((prev) => [...prev, { id: `u-${Date.now()}`, role: 'user', text }]);
    if (/^(done|finish|that.?s all|stop|no more)/i.test(text)) {
      appendSiggy(
        `${addedCount > 0 ? `Wonderful, I have **${addedCount}** medicine${addedCount === 1 ? '' : 's'} on your list.` : 'No problem.'} You can add more any time from the Medicines page. Taking you home now.`,
      );
      // Send to Medications (not Home), so a user who finished the
      // flow without adding any medicines does not get bounced back
      // to Welcome by the new-user redirect on Home.
      setTimeout(() => navigate('/medications'), 1800);
      return;
    }
    setBusy(true);
    try {
      const res = await authedFetch('/api/onboarding/parse-med', {
        method: 'POST',
        body: JSON.stringify({ text }),
      });
      const json = (await res.json()) as {
        ok: boolean;
        data?: ParsedMed;
        error?: { message: string };
      };
      if (!json.ok || !json.data) {
        setError(json.error?.message ?? 'Could not understand that.');
        appendSiggy('Sorry, I did not catch that. Tell me the medicine name. You can speak it.');
        return;
      }
      const parsed = json.data;
      if (parsed.confidence === 'low' || !parsed.name) {
        appendSiggy(
          "I am not sure which medicine that is. Try saying just the name. For example, **'Atorvastatin'**.",
        );
        return;
      }
      setTurns((prev) => [
        ...prev,
        {
          id: `p-${Date.now()}`,
          role: 'parse',
          parsed,
          pendingText: text,
        },
      ]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function confirmAdd(parsed: ParsedMed) {
    setBusy(true);
    setError(null);
    try {
      const res = await authedFetch('/api/medications', {
        method: 'POST',
        body: JSON.stringify({
          name: parsed.name,
          frequency: parsed.frequency ?? 'as directed',
          strength: parsed.strength ?? undefined,
          addedVia: 'MANUAL',
        }),
      });
      const json = (await res.json()) as { ok: boolean; error?: { message: string } };
      if (!json.ok) {
        setError(json.error?.message ?? 'Could not add the medicine.');
        return;
      }
      setAddedCount((c) => c + 1);
      appendSiggy(
        `Got it, **${parsed.name}** is on your list. Tell me about another medicine, or say **done** when you have added everything.`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  function rejectAdd() {
    appendSiggy('No problem. Tell me the medicine again, slowly. I will listen for the name.');
  }

  const startListening = () => {
    if (speech.state === 'listening') {
      speech.stop();
      return;
    }
    baseline.current = '';
    speech.start((text, isFinal) => {
      const prefix = baseline.current ? `${baseline.current.trimEnd()} ` : '';
      const next = `${prefix}${text}`;
      setDraft(next);
      if (isFinal) baseline.current = next;
    });
  };

  return (
    <AppPage title="Welcome">
      <WelcomeHero />
      <div className="chat-thread" aria-live="polite">
        {turns.map((t) => (
          <TurnRow
            key={t.id}
            turn={t}
            onConfirm={confirmAdd}
            onReject={rejectAdd}
            disabled={busy}
          />
        ))}
        {busy ? (
          <div className="chat-row chat-row--siggy">
            <span className="chat-siggy-avatar">
              <SiggyAvatar size={40} />
            </span>
            <div className="chat-bubble chat-bubble--typing">
              <div className="chat-typing">
                <span />
                <span />
                <span />
              </div>
            </div>
          </div>
        ) : null}
        <div ref={endRef} />
      </div>

      {error ? (
        <div className="account-section">
          <p style={{ color: 'var(--color-danger)', margin: 0 }}>{error}</p>
        </div>
      ) : null}

      <div className="chat-composer">
        <input
          ref={composerRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Type or speak the medicine"
          disabled={busy}
          aria-label="What medicine do you want to add"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void handleSubmit();
            }
          }}
          style={{
            width: '100%',
            padding: '14px 16px',
            fontSize: 18,
            minHeight: 48,
            border: '1.5px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
          }}
        />
        <div className="chat-composer__actions">
          {speech.supported ? (
            <button
              type="button"
              className={`mic-btn${speech.state === 'listening' ? ' is-listening' : ''}`}
              onClick={startListening}
              disabled={busy}
              aria-label={speech.state === 'listening' ? 'Stop listening' : 'Speak'}
            >
              <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
                <title>Microphone</title>
                <path
                  d="M12 3a4 4 0 0 0-4 4v5a4 4 0 0 0 8 0V7a4 4 0 0 0-4-4z"
                  fill="currentColor"
                />
                <path
                  d="M5 11v1a7 7 0 0 0 14 0v-1M12 19v3M9 22h6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          ) : (
            <span />
          )}
          <SeniorButton
            onClick={() => void handleSubmit()}
            variant="primary"
            fullWidth
            disabled={busy || draft.trim().length === 0}
          >
            Send
          </SeniorButton>
        </div>
      </div>
    </AppPage>
  );
}

/**
 * Welcome hero. Senior-tuned: opt-in (not autoplay) 24s intro video
 * generated with Veo 2, plus a soft tap-to-hear Siggy welcome
 * voiceover. Both are entirely optional — a user who scrolls past
 * goes straight into the conversational onboarding below.
 *
 * The video is muted by default (browser autoplay-with-sound is
 * blocked anyway). The voiceover button speaks Siggy's hi line via
 * a hidden <audio> element so screen-reader users get the same
 * affordance via the labeled button.
 */
function WelcomeHero(): JSX.Element {
  return (
    <section
      style={{
        marginBottom: 'var(--space-md)',
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
        border: '1px solid var(--color-border)',
      }}
    >
      <video
        src="/video/welcome-24s.mp4"
        poster="/illustrations/video-seeds/welcome-shot-1-seed.png"
        controls
        muted
        playsInline
        preload="metadata"
        style={{
          width: '100%',
          height: 'auto',
          display: 'block',
          background: '#000',
        }}
      />
      <div style={{ padding: 'var(--space-md)' }}>
        <p
          style={{
            margin: 0,
            fontSize: 16,
            color: 'var(--color-text-secondary)',
            lineHeight: 1.45,
          }}
        >
          Tap play above to watch a 24-second intro. Or scroll down and start chatting with Siggy
          below.
        </p>
        <SiggyHelloButton />
      </div>
    </section>
  );
}

function SiggyHelloButton(): JSX.Element {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => {
          const el = audioRef.current;
          if (!el) return;
          if (playing) {
            el.pause();
            el.currentTime = 0;
            setPlaying(false);
          } else {
            void el.play();
          }
        }}
        style={{
          marginTop: 12,
          minHeight: 48,
          padding: '0 18px',
          background: 'transparent',
          border: '1.5px solid var(--color-border)',
          borderRadius: 12,
          fontSize: 15,
          fontWeight: 600,
          color: 'var(--color-text-primary)',
          cursor: 'pointer',
        }}
      >
        {playing ? 'Stop' : 'Hear Siggy say hi'}
      </button>
      {/* biome-ignore lint/a11y/useMediaCaption: voiceover content is the button label itself, no separate caption needed */}
      <audio
        ref={audioRef}
        src="/audio/siggy-welcome.mp3"
        preload="none"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />
    </>
  );
}

function TurnRow({
  turn,
  onConfirm,
  onReject,
  disabled,
}: {
  turn: Turn;
  onConfirm: (m: ParsedMed) => void;
  onReject: () => void;
  disabled: boolean;
}): JSX.Element {
  if (turn.role === 'user') {
    return (
      <div className="chat-row chat-row--user">
        <div className="chat-bubble">
          <div className="chat-bubble__author">You</div>
          <p className="chat-bubble__text">{turn.text}</p>
        </div>
      </div>
    );
  }
  if (turn.role === 'siggy') {
    return (
      <div className="chat-row chat-row--siggy">
        <span className="chat-siggy-avatar">
          <SiggyAvatar size={40} />
        </span>
        <div className="chat-bubble">
          <div className="chat-bubble__author">Siggy</div>
          <ChatMarkdown text={turn.text} />
        </div>
      </div>
    );
  }
  // parse turn
  const p = turn.parsed;
  return (
    <div className="chat-row chat-row--siggy">
      <span className="chat-siggy-avatar">
        <SiggyAvatar size={40} />
      </span>
      <div className="chat-bubble">
        <div className="chat-bubble__author">Siggy</div>
        <p style={{ margin: '0 0 var(--space-sm)' }}>
          Got it. I heard <strong>{p.name}</strong>
          {p.strength ? (
            <>
              , <strong>{p.strength}</strong>
            </>
          ) : null}
          {p.frequency ? (
            <>
              , <strong>{p.frequency}</strong>
            </>
          ) : null}
          . Add it to your list?
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            className="home-hero__cta"
            style={{ minHeight: 44, padding: '8px 18px', fontSize: 16 }}
            onClick={() => onConfirm(p)}
            disabled={disabled}
          >
            Yes, add it
          </button>
          <button
            type="button"
            className="ghost-cta"
            style={{ minHeight: 44, padding: '8px 18px', fontSize: 16 }}
            onClick={onReject}
            disabled={disabled}
          >
            No, try again
          </button>
        </div>
      </div>
    </div>
  );
}
